import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseTable } from '@/components';
import { useNotificationMethodsTable } from '@/components/pages/SettingsPage/MainContent/UserManagement/NotificationMethods/useNotificationMethodsTable';
import { Skeleton } from '@/components/ui/skeleton';
import {
  restoreDeliveryMethods,
  updateNotificationMethodsCache,
} from '@/components/pages/SettingsPage/MainContent/UserManagement/NotificationMethods/utils';
import { SectionEmptyState } from '@/components/pages/AssetsPreviewPage/AssetsOverview/EmptyStates';
import { isEqual } from 'lodash';
import { useParams } from 'react-router-dom';
import { dispatch } from '@/store/store';

export interface NotificationMethodsTableRef {
  triggerSave: () => void;
}

interface NotificationMethodsTableProps {
  setIsDirty: any;
}

export const NotificationMethodsTable = forwardRef<NotificationMethodsTableRef, NotificationMethodsTableProps>(
  ({ setIsDirty }, ref) => {
    const { t } = useTranslation();
    const { columns, data: initialData, isLoading, updateNotificationDeliveryMethods } = useNotificationMethodsTable();
    const [currentData, setCurrentData] = useState<any>(initialData);
    const { userId } = useParams<{ userId: string }>();
    const updateData = (rowIndex: number, columnId: string, value: boolean) => {
      setCurrentData((oldData: any) =>
        oldData.map((row: any, index: number) => {
          if (index === rowIndex) {
            return {
              ...row,
              [columnId]: value,
            };
          }
          return row;
        })
      );
    };

    useImperativeHandle(ref, () => ({
      triggerSave: async () => {
        const payload = currentData.map(restoreDeliveryMethods);
        dispatch(updateNotificationMethodsCache(payload, userId));
        setIsDirty(false);
        await updateNotificationDeliveryMethods({ payload });
      },
    }));

    useEffect(() => {
      setIsDirty(!isEqual(currentData, initialData));
    }, [currentData, initialData]);

    useEffect(() => {
      if (initialData?.length) {
        setCurrentData(initialData);
      }
    }, [initialData]);

    if (isLoading) {
      return <Skeleton className="h-[calc(100vh-15.3rem)]" />;
    }

    if (!initialData?.length) {
      return <SectionEmptyState text={t('general.noData')} />;
    }

    return (
      <BaseTable
        columns={columns}
        data={currentData}
        wrapHeaders={false}
        meta={{ updateData }}
        classNames={{
          card: {
            container: 'h-full w-full overflow-x-auto max-h-full max-w-[calc(100%)] overflow-x-auto rounded-lg',
            content: 'p-0 m-2 overflow-y-hidden max-h-full ',
          },
          table: {
            container:
              'overflow-auto h-full w-full sticky top-0  min-h-[calc(100vh-16.3rem)] max-h-[calc(100vh-15.8rem)]',
            th: '[&>*]:text-left [&>*]:text-subtext [&>*]:font-medium [&>*]:text-[14px] py-2',
            tr: 'h-14 bg-white ',
            td: 'group-hover:bg-gray-100 text-[14px]',
          },
        }}
      />
    );
  }
);

NotificationMethodsTable.displayName = 'NotificationMethodsTable';
